import { AxiosError } from 'axios';

// i18n API 에러 타입 정의
export interface I18nApiError {
  field?: string;
  language?: string;
  message: string;
  code?: string;
}

export interface I18nValidationError {
  [field: string]: {
    [language: string]: string[];
  };
}

// i18n 관련 에러 메시지
export const I18N_ERROR_MESSAGES = {
  REQUIRED_KOREAN: '한국어는 필수 입력 항목입니다',
  INVALID_LANGUAGE: (lang: string) => `지원되지 않는 언어 코드입니다: ${lang}`,
  MAX_LENGTH_EXCEEDED: (field: string, maxLength: number) =>
    `${field} 필드는 최대 ${maxLength}자까지 입력 가능합니다`,
  SYNC_ERROR: '레거시 필드와 i18n 필드 동기화에 실패했습니다',
  VALIDATION_ERROR: '다국어 데이터 검증에 실패했습니다',
  NETWORK_ERROR: '네트워크 오류가 발생했습니다',
  SERVER_ERROR: '서버 오류가 발생했습니다',
  UNAUTHORIZED: '인증이 필요합니다',
  FORBIDDEN: '접근 권한이 없습니다',
  NOT_FOUND: '요청한 콘텐츠를 찾을 수 없습니다',
  CONFLICT: '데이터 충돌이 발생했습니다',
} as const;

/**
 * i18n API 에러 처리 클래스
 */
export class I18nApiErrorHandler {
  /**
   * Axios 에러를 i18n 에러로 변환
   */
  static handleAxiosError(error: AxiosError): I18nApiError[] {
    if (!error.response) {
      return [
        {
          message: I18N_ERROR_MESSAGES.NETWORK_ERROR,
          code: 'NETWORK_ERROR',
        },
      ];
    }

    const { status, data } = error.response;

    switch (status) {
      case 400:
        return this.handleValidationError(data);
      case 401:
        return [
          {
            message: I18N_ERROR_MESSAGES.UNAUTHORIZED,
            code: 'UNAUTHORIZED',
          },
        ];
      case 403:
        return [
          {
            message: I18N_ERROR_MESSAGES.FORBIDDEN,
            code: 'FORBIDDEN',
          },
        ];
      case 404:
        return [
          {
            message: I18N_ERROR_MESSAGES.NOT_FOUND,
            code: 'NOT_FOUND',
          },
        ];
      case 409:
        return [
          {
            message: I18N_ERROR_MESSAGES.CONFLICT,
            code: 'CONFLICT',
          },
        ];
      case 500:
      default:
        return [
          {
            message: I18N_ERROR_MESSAGES.SERVER_ERROR,
            code: 'SERVER_ERROR',
          },
        ];
    }
  }

  /**
   * 검증 에러 처리 (400 에러)
   */
  private static handleValidationError(data: any): I18nApiError[] {
    const errors: I18nApiError[] = [];

    if (data && typeof data === 'object') {
      // i18n 필드 검증 에러 처리
      Object.keys(data).forEach(field => {
        if (field.startsWith('i18n_')) {
          const i18nErrors = data[field];
          if (typeof i18nErrors === 'object') {
            Object.keys(i18nErrors).forEach(language => {
              const languageErrors = i18nErrors[language];
              if (Array.isArray(languageErrors)) {
                languageErrors.forEach(message => {
                  errors.push({
                    field,
                    language,
                    message: this.translateValidationMessage(message, field, language),
                    code: 'VALIDATION_ERROR',
                  });
                });
              }
            });
          }
        } else {
          // 레거시 필드 검증 에러 처리
          const fieldErrors = data[field];
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(message => {
              errors.push({
                field,
                message: this.translateValidationMessage(message, field),
                code: 'VALIDATION_ERROR',
              });
            });
          }
        }
      });
    }

    if (errors.length === 0) {
      errors.push({
        message: I18N_ERROR_MESSAGES.VALIDATION_ERROR,
        code: 'VALIDATION_ERROR',
      });
    }

    return errors;
  }

  /**
   * 검증 메시지를 한국어로 번역
   */
  private static translateValidationMessage(
    message: string,
    field?: string,
    language?: string
  ): string {
    // 공통 검증 메시지 번역
    if (message.includes('required') || message.includes('필수')) {
      if (language === 'ko') {
        return I18N_ERROR_MESSAGES.REQUIRED_KOREAN;
      }
      return `${field} 필드는 필수 입력 항목입니다`;
    }

    if (message.includes('max_length') || message.includes('최대')) {
      const maxLengthMatch = message.match(/(\d+)/);
      const maxLength = maxLengthMatch ? parseInt(maxLengthMatch[1]) : 255;
      return I18N_ERROR_MESSAGES.MAX_LENGTH_EXCEEDED(field || '필드', maxLength);
    }

    if (message.includes('invalid') || message.includes('잘못된')) {
      if (language) {
        return I18N_ERROR_MESSAGES.INVALID_LANGUAGE(language);
      }
      return `${field} 필드의 값이 올바르지 않습니다`;
    }

    // 원본 메시지 반환
    return message;
  }

  /**
   * 에러 메시지를 사용자 친화적인 형태로 포맷
   */
  static formatErrorsForUser(errors: I18nApiError[]): string {
    if (errors.length === 0) {
      return I18N_ERROR_MESSAGES.VALIDATION_ERROR;
    }

    if (errors.length === 1) {
      return errors[0].message;
    }

    // 여러 에러가 있는 경우 목록으로 표시
    const errorMessages = errors.map((error, index) => {
      let message = `${index + 1}. ${error.message}`;
      if (error.field && error.language) {
        message += ` (${error.field} - ${error.language})`;
      } else if (error.field) {
        message += ` (${error.field})`;
      }
      return message;
    });

    return errorMessages.join('\n');
  }

  /**
   * 필드별 에러 그룹화
   */
  static groupErrorsByField(errors: I18nApiError[]): Record<string, I18nApiError[]> {
    return errors.reduce(
      (groups, error) => {
        const key = error.field || 'general';
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(error);
        return groups;
      },
      {} as Record<string, I18nApiError[]>
    );
  }

  /**
   * 언어별 에러 그룹화
   */
  static groupErrorsByLanguage(errors: I18nApiError[]): Record<string, I18nApiError[]> {
    return errors.reduce(
      (groups, error) => {
        const key = error.language || 'general';
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(error);
        return groups;
      },
      {} as Record<string, I18nApiError[]>
    );
  }
}
